import { useState, useEffect } from 'react';
import { useEditLock } from '../hooks/useEditLock';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { LockBanner } from '../components/permissions/LockBanner';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { broadcastDataChange } from '../lib/dataBus';
import { useAuth } from '../contexts/AuthContext';
import { useIsPhone } from '../hooks/useIsPhone';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CircleDot, User, MapPin, Loader2, FileText, CheckCircle2, Eye, Download, MoreHorizontal, ClipboardList, Truck, CalendarDays, Plus, Minus, Save, Package, Clock, Euro, CreditCard, ShieldCheck, Warehouse, Calendar, Pencil, Check, X, MessageSquare } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const CLIENT_STATUTS = {
    a_verifier: { label: 'A verifier', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    pret: { label: 'Pret', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    annule: { label: 'Annule', color: 'bg-red-100 text-red-700 border-red-200' },
};

const STATUS_BORDER_COLORS = {
    a_verifier: 'border-amber-300',
    pret: 'border-emerald-400',
    annule: 'border-red-400',
};

const FORMULE_LABELS = {
    economique: 'Economique',
    economique_plus: 'Economique Plus',
    standard: 'Standard',
    luxe: 'Luxe',
};

const FORMULE_COLORS = {
    economique: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'text-emerald-500' },
    economique_plus: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', icon: 'text-cyan-500' },
    standard: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-500' },
    luxe: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: 'text-violet-500' },
};

function formatDate(dateStr) {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr }); } catch { return dateStr; }
}

export default function ClientDetailPage() {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const isPhone = useIsPhone();
    // phoneReadOnly retire : tous les utilisateurs (admin, commercial,
    // assistante) peuvent modifier la fiche client sur tous les appareils
    // (telephone, tablette, PC). Les permissions par module sont deja
    // gerees par les ReadOnlyGuard via la permission "clients".
    const phoneReadOnly = false;
    const lock = useEditLock('client', clientId, !!clientId);
    const [client, setClient] = useState(null);
    const [devis, setDevis] = useState(null);
    const [inventaire, setInventaire] = useState(null);
    const [programmation, setProgrammation] = useState({ nb_jours: 0, jours: [] });
    const [savingProg, setSavingProg] = useState(false);
    const [editingProg, setEditingProg] = useState(false);
    const [livraisonData, setLivraisonData] = useState({ fournitures_active: false, fournitures: {}, livraison: null, livraisons_supp: [] });
    const [livraisonForm, setLivraisonForm] = useState({ date: '', heure: '' });
    const [showLivraisonForm, setShowLivraisonForm] = useState(false);
    // Livraisons supplémentaires (ajoutées après que la principale soit terminée)
    const [livraisonSuppForm, setLivraisonSuppForm] = useState({ date: '', heure: '', remarque: '' });
    const [showLivraisonSuppForm, setShowLivraisonSuppForm] = useState(false);
    const [editingLivraisonSuppId, setEditingLivraisonSuppId] = useState(null);
    const [savingLivraisonSupp, setSavingLivraisonSupp] = useState(false);
    const [savingLivraison, setSavingLivraison] = useState(false);
    const [loading, setLoading] = useState(true);
    const [facture, setFacture] = useState(null);
    const [acomptePaiements, setAcomptePaiements] = useState([]);
    const [pdfPreview, setPdfPreview] = useState(null);
    const [aotList, setAotList] = useState([]);
    const [monteMeubleList, setMonteMeubleList] = useState([]);
    const [affectations, setAffectations] = useState({
        aot_depart: { jours: [], active: false },
        aot_arrivee: { jours: [], active: false },
        mm_depart: { jours: [], active: false },
        mm_arrivee: { jours: [], active: false },
        gm: { jours: [], active: false },
    });
    const [gardeMeubleList, setGardeMeubleList] = useState([]);
    const [editingRemarque, setEditingRemarque] = useState(false);
    const [remarqueAccepteValue, setRemarqueAccepteValue] = useState('');
    const [savingRemarque, setSavingRemarque] = useState(false);

    const saveRemarqueAccepte = async () => {
        if (savingRemarque) return; // anti double-clic
        setSavingRemarque(true);
        try {
            await axios.patch(
                `${API}/api/clients/${clientId}/remarque-accepte`,
                { remarque_accepte: remarqueAccepteValue },
                { withCredentials: true },
            );
            toast.success('Remarque mise à jour');
            // Optimistic update : la nouvelle valeur s'affiche immédiatement
            // sans attendre le re-fetch complet (qui peut être lent).
            setClient(prev => prev ? { ...prev, remarque_accepte: remarqueAccepteValue } : prev);
            setEditingRemarque(false);
            // Re-fetch silencieux en arrière-plan (sans loader) pour resynchroniser
            // d'éventuels champs dérivés.
            fetchClient();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSavingRemarque(false);
        }
    };

    const fetchClient = async () => {
        setLoading(true);
        try {
            const { data: clientData } = await axios.get(`${API}/api/clients/${clientId}`, { withCredentials: true });

            // Charge tout en parallèle, attend que TOUT soit prêt avant d'afficher la page
            // (évite les "flashes" de re-renders successifs).
            const [
                devisRes,
                inventaireRes,
                programmationRes,
                livraisonRes,
                facturesRes,
                aotRes,
                monteMeubleRes,
                gardeMeubleRes,
                affectationsRes,
            ] = await Promise.all([
                clientData.devis_id
                    ? axios.get(`${API}/api/devis/${clientData.devis_id}`, { withCredentials: true }).catch(() => null)
                    : Promise.resolve(null),
                clientData.inventaire_id
                    ? axios.get(`${API}/api/inventaires/${clientData.inventaire_id}`, { withCredentials: true }).catch(() => null)
                    : Promise.resolve(null),
                axios.get(`${API}/api/clients/${clientId}/programmation`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/clients/${clientId}/livraison`, { withCredentials: true }).catch(() => null),
                clientData.inventaire_id
                    ? axios.get(`${API}/api/factures`, { withCredentials: true }).catch(() => null)
                    : Promise.resolve(null),
                axios.get(`${API}/api/aot`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/monte-meuble`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/garde-meuble`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/clients/${clientId}/programmation-affectations`, { withCredentials: true }).catch(() => null),
            ]);

            // Facture liée + paiements
            let factureFound = null;
            let paiements = [];
            if (facturesRes?.data) {
                factureFound = facturesRes.data.find(f => f.inventaire_id === clientData.inventaire_id && !f.description) || null;
                if (factureFound) {
                    try {
                        const p = await axios.get(`${API}/api/factures/${factureFound.id}/paiements`, { withCredentials: true });
                        paiements = p.data || [];
                    } catch { /* ignore */ }
                }
            }

            // Application atomique de tous les états (1 seul re-render)
            setClient(clientData);
            setDevis(devisRes?.data || null);
            setInventaire(inventaireRes?.data || null);
            setProgrammation(programmationRes?.data || { nb_jours: 0, jours: [] });
            setLivraisonData(livraisonRes?.data || { fournitures_active: false, fournitures: {}, livraison: null });
            setFacture(factureFound);
            setAcomptePaiements(paiements);
            setAotList((aotRes?.data || []).filter(a => a.client_nom === clientData.client_nom));
            setMonteMeubleList((monteMeubleRes?.data || []).filter(m => m.client_nom === clientData.client_nom));
            setGardeMeubleList((gardeMeubleRes?.data || []).filter(g => g.client_nom === clientData.client_nom));
            setAffectations(affectationsRes?.data || {
                aot_depart: { jours: [], active: false },
                aot_arrivee: { jours: [], active: false },
                mm_depart: { jours: [], active: false },
                mm_arrivee: { jours: [], active: false },
                gm: { jours: [], active: false },
            });
        } catch (e) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchClient(); }, [clientId]);

    // If URL contains #programmation, scroll to that section after data loads
    useEffect(() => {
        if (!loading && client && window.location.hash === '#programmation') {
            const t = setTimeout(() => {
                const el = document.getElementById('programmation');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 200);
            return () => clearTimeout(t);
        }
    }, [loading, client]);

    const handleStatutChange = async (field, value) => {
        try {
            await axios.patch(`${API}/api/clients/${clientId}/statut`, { field, value }, { withCredentials: true });
            toast.success('Statut mis a jour');
            broadcastDataChange('client', clientId, { field, value });
            fetchClient();
        } catch { toast.error('Erreur'); }
    };

    // ---- Statut auto (a_verifier <-> pret) ---------------------------------
    // Devient "pret" si les sections 4, 5, 6, 7, 8, 9 sont toutes "vertes".
    // Repasse à "a_verifier" si une seule devient non-verte.
    // Le statut "annule" est manuel et n'est jamais écrasé automatiquement.
    // Section 4 — Programmation : tous les jours doivent avoir une date ET toutes
    // les options actives (AOT/MM/GM) doivent avoir au moins un jour coché.
    const allJoursHaveDates = programmation.jours.length > 0 && programmation.jours.every(j => j.date);
    const activeAffectKeys = ['aot_depart', 'aot_arrivee', 'mm_depart', 'mm_arrivee', 'gm']
        .filter(k => affectations[k]?.active);
    const allAffectsAssigned = activeAffectKeys.every(k => (affectations[k]?.jours || []).length > 0);
    const programmationOK = allJoursHaveDates && allAffectsAssigned;
    // Section 5 — Livraison : pas de fournitures OU livraison programmée.
    const livraisonOK = !livraisonData.fournitures_active || !!livraisonData.livraison;
    // Section 6 — Acompte : au moins un paiement OU acompte = "effectue".
    const acompteOK = acomptePaiements.length > 0 || client?.acompte === 'effectue';
    // Section 7 — AOT : aucune AOT OU toutes les AOT sont "accepte".
    const aotOK = aotList.length === 0 || aotList.every(a => a.statut === 'accepte');
    // Section 8 — Monte-meuble : aucun MM OU toutes les MM sont "reserve" ou "termine".
    const monteMeubleOK = monteMeubleList.length === 0
        || monteMeubleList.every(mm => mm.statut === 'reserve' || mm.statut === 'termine');
    // Section 9 — Garde-meuble : aucun GM OU statuts différents de "a_prevoir".
    const gardeMeubleOK = gardeMeubleList.length === 0
        || gardeMeubleList.every(gm => gm.statut !== 'a_prevoir');
    const allGreen = programmationOK && livraisonOK && acompteOK && aotOK && monteMeubleOK && gardeMeubleOK;

    useEffect(() => {
        if (!client) return;
        if (client.statut_client === 'annule') return; // l'annulation est manuelle
        // ⚠️ Ne pas déclencher pendant l'édition d'une section (programmation, acompte, livraison…)
        // sinon le fetchClient() qui suit écrase le state local en cours de saisie.
        if (editingProg || editingRemarque || showLivraisonForm) return;
        const target = allGreen ? 'pret' : 'a_verifier';
        if (client.statut_client !== target) {
            axios
                .patch(`${API}/api/clients/${clientId}/statut`, { field: 'statut_client', value: target }, { withCredentials: true })
                .then(() => setClient(prev => prev ? { ...prev, statut_client: target } : prev))
                .catch(() => { /* silencieux */ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allGreen, client?.statut_client, editingProg, editingRemarque, showLivraisonForm]);

    const handleDownloadPdf = (devisId) => {
        window.open(`${API}/api/devis/${devisId}/pdf`, '_blank');
    };

    const pdfAbortRef = useState(() => ({ current: null }))[0];
    const [pdfLoading, setPdfLoading] = useState(null); // 'inventaire' | 'lettre' | null

    const handlePreviewInventaire = async () => {
        if (!client?.inventaire_id) { toast.error('Aucun inventaire lie'); return; }
        if (pdfAbortRef.current) pdfAbortRef.current.abort();
        const controller = new AbortController();
        pdfAbortRef.current = controller;
        setPdfLoading('inventaire');
        const toastId = toast.loading('Génération PDF inventaire en cours…');
        try {
            const res = await axios.get(`${API}/api/inventaires/${client.inventaire_id}/pdf`, { withCredentials: true, responseType: 'blob', signal: controller.signal });
            if (controller.signal.aborted) return;
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            setPdfPreview({ url, title: `Inventaire — ${client.client_nom}`, filename: `Inventaire_${client.client_nom}.pdf` });
            toast.dismiss(toastId);
        } catch (e) {
            toast.dismiss(toastId);
            if (axios.isCancel(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
            toast.error('Erreur generation PDF inventaire');
        } finally {
            if (pdfAbortRef.current === controller) pdfAbortRef.current = null;
            setPdfLoading(prev => (prev === 'inventaire' ? null : prev));
        }
    };

    const handlePreviewLettre = async () => {
        if (!client) return;
        if (pdfAbortRef.current) pdfAbortRef.current.abort();
        const controller = new AbortController();
        pdfAbortRef.current = controller;
        setPdfLoading('lettre');
        const toastId = toast.loading('Génération lettre de voiture en cours…');
        try {
            const res = await axios.get(`${API}/api/clients/${client.id}/lettre-voiture/pdf`, { withCredentials: true, responseType: 'blob', signal: controller.signal });
            if (controller.signal.aborted) return;
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            setPdfPreview({
                url,
                title: `Lettre de voiture — ${client.client_nom}`,
                filename: `Lettre_de_voiture_${client.client_nom}.pdf`,
            });
            toast.dismiss(toastId);
        } catch (e) {
            toast.dismiss(toastId);
            if (axios.isCancel(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
            toast.error('Erreur generation lettre de voiture');
        } finally {
            if (pdfAbortRef.current === controller) pdfAbortRef.current = null;
            setPdfLoading(prev => (prev === 'lettre' ? null : prev));
        }
    };

    const handleBack = () => {
        // Annule une génération PDF en cours pour libérer la navigation immédiatement
        if (pdfAbortRef.current) {
            pdfAbortRef.current.abort();
            pdfAbortRef.current = null;
            setPdfLoading(null);
            toast.dismiss();
        }
        // Retour contextuel : si on arrive depuis le calendrier prestation,
        // on y retourne au lieu d'aller vers la liste clients.
        const from = location.state?.from;
        if (from === 'calendrier-prestation') {
            navigate('/calendrier-prestation');
            return;
        }
        navigate('/clients');
    };

    const handleDownloadPreview = () => {
        if (!pdfPreview) return;
        const a = document.createElement('a');
        a.href = pdfPreview.url;
        a.download = pdfPreview.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('PDF telecharge');
    };

    const handleClosePreview = () => {
        if (pdfPreview?.url) window.URL.revokeObjectURL(pdfPreview.url);
        setPdfPreview(null);
    };

    const TYPE_PAIEMENT_LABELS = { cheque_o: 'Cheque O', cheque_so: 'Cheque SO', virement: 'Virement', espece: 'Espece' };

    const TYPES_PRESTATION = [
        { value: 'preparation', label: 'Preparation' },
        { value: 'preparation_chargement', label: 'Preparation et chargement' },
        { value: 'chargement', label: 'Chargement' },
        { value: 'dechargement', label: 'Dechargement' },
        { value: 'demenagement_local', label: 'Demenagement local' },
        { value: 'stockage', label: 'Stockage' },
        { value: 'destockage', label: 'Destockage' },
        { value: 'route_journee', label: 'Route sur une journee' },
    ];

    const handleNbJoursChange = (nb) => {
        const n = Math.max(0, Math.min(30, nb));
        const jours = [];
        for (let i = 1; i <= n; i++) {
            const existing = programmation.jours.find(j => j.jour === i);
            jours.push(existing || { jour: i, date: '', heure: '', nb_demenageurs: null, camion_20m3: 0, camion_30m3: 0, type_prestation: '' });
        }
        setProgrammation({ nb_jours: n, jours });
    };

    const updateJour = (jourNum, field, value) => {
        setProgrammation(prev => ({
            ...prev,
            jours: prev.jours.map(j => j.jour === jourNum ? { ...j, [field]: value } : j)
        }));
    };

    const saveProgrammation = async () => {
        setSavingProg(true);
        try {
            await axios.put(`${API}/api/clients/${clientId}/programmation`, programmation, { withCredentials: true });
            // Save AOT/MM/GM affectations as well
            const payload = {
                aot_depart: affectations.aot_depart.jours || [],
                aot_arrivee: affectations.aot_arrivee.jours || [],
                mm_depart: affectations.mm_depart.jours || [],
                mm_arrivee: affectations.mm_arrivee.jours || [],
                gm: affectations.gm.jours || [],
            };
            await axios.put(`${API}/api/clients/${clientId}/programmation-affectations`, payload, { withCredentials: true });
            toast.success('Programmation enregistree');
            broadcastDataChange('client', clientId, { field: 'programmation' });
            setEditingProg(false);
            fetchClient();
        } catch { toast.error('Erreur'); }
        finally { setSavingProg(false); }
    };

    const toggleAffectation = (key, jourNum) => {
        setAffectations(prev => {
            const current = prev[key]?.jours || [];
            const exists = current.includes(jourNum);
            const next = exists ? current.filter(j => j !== jourNum) : [...current, jourNum].sort((a, b) => a - b);
            return { ...prev, [key]: { ...prev[key], jours: next } };
        });
    };

    const handleConfirmLivraison = async () => {
        if (!livraisonForm.date || !livraisonForm.heure) { toast.error('Veuillez saisir la date et l\'heure'); return; }
        setSavingLivraison(true);
        try {
            await axios.post(`${API}/api/clients/${clientId}/livraison`, livraisonForm, { withCredentials: true });
            toast.success('Livraison programmee');
            broadcastDataChange('client', clientId, { field: 'livraison' });
            setShowLivraisonForm(false);
            fetchClient();
        } catch { toast.error('Erreur'); }
        finally { setSavingLivraison(false); }
    };

    const handleConfirmLivraisonSupp = async () => {
        if (!livraisonSuppForm.date || !livraisonSuppForm.heure) {
            toast.error('Veuillez saisir la date et l\'heure'); return;
        }
        setSavingLivraisonSupp(true);
        try {
            if (editingLivraisonSuppId) {
                await axios.put(
                    `${API}/api/clients/${clientId}/livraisons-supp/${editingLivraisonSuppId}`,
                    livraisonSuppForm,
                    { withCredentials: true },
                );
                toast.success('Livraison supplémentaire modifiée');
            } else {
                await axios.post(
                    `${API}/api/clients/${clientId}/livraisons-supp`,
                    livraisonSuppForm,
                    { withCredentials: true },
                );
                toast.success('Livraison supplémentaire ajoutée');
            }
            broadcastDataChange('client', clientId, { field: 'livraison_supp' });
            setShowLivraisonSuppForm(false);
            setEditingLivraisonSuppId(null);
            setLivraisonSuppForm({ date: '', heure: '', remarque: '' });
            fetchClient();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        } finally {
            setSavingLivraisonSupp(false);
        }
    };

    const FOURNITURE_LABELS = {
        cartons_standards: 'Cartons standards',
        cartons_livres: 'Cartons livres',
        cartons_verres: 'Cartons verres',
        cartons_bouteilles: 'Cartons bouteilles',
        adhesif: 'Adhesif',
        bulle_grande: 'Papier bulle (grande)',
        bulle_petite: 'Papier bulle (petite)',
        autres: 'Autres',
    };

    if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>;
    if (!client) return <div className="text-center py-24 text-slate-500">Client non trouve</div>;

    const statutInfo = CLIENT_STATUTS[client.statut_client] || CLIENT_STATUTS.a_verifier;
    const borderColor = STATUS_BORDER_COLORS[client.statut_client] || 'border-amber-300';
    const formuleColors = FORMULE_COLORS[client.formule] || FORMULE_COLORS.standard;
    const formuleLabel = FORMULE_LABELS[client.formule] || client.formule;

    return (
        <div className="flex flex-col h-full" data-testid="client-detail-page">
            {lock.blocked && <LockBanner userName={lock.user_name} expiresAt={lock.expires_at} />}
            {/* Sticky Header — Retour + Téléchargements restent TOUJOURS actifs, même en lecture seule */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" onClick={handleBack} data-testid="client-back-btn">
                            <ArrowLeft size={16} className="mr-2" />
                            Retour
                        </Button>
                        <h1 className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Apercu client — {client.client_nom}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handlePreviewInventaire} disabled={pdfLoading === 'inventaire'} data-testid="client-btn-inventaire">
                            {pdfLoading === 'inventaire' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ClipboardList size={16} className="mr-2" />}
                            Telecharger inventaire
                        </Button>
                        <Button variant="outline" size="sm" onClick={handlePreviewLettre} disabled={pdfLoading === 'lettre'} data-testid="client-btn-lettre-voiture">
                            {pdfLoading === 'lettre' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Truck size={16} className="mr-2" />}
                            Telecharger LV
                        </Button>
                    </div>
                </div>
            </div>
            <ReadOnlyGuard module="clients" lockBlocked={lock.blocked} forceReadOnly={phoneReadOnly}>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                <div className="max-w-5xl mx-auto space-y-6">
                    {/* Section 1: Statut client */}
                    <Card className={`bg-blue-50 border-2 ${borderColor}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                                <CircleDot size={16} />
                                1. Statut du client
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4">
                                <Select value={client.statut_client || 'a_verifier'} onValueChange={v => handleStatutChange('statut_client', v)}>
                                    <SelectTrigger className="w-[250px] bg-white" data-testid="client-statut-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* L'utilisateur ne peut basculer que vers "annulé" (depuis a_verifier ou pret).
                                            Les transitions a_verifier <-> pret sont gérées automatiquement.
                                            Depuis "annulé", on autorise le retour à "a_verifier" pour réactiver. */}
                                        {(() => {
                                            const current = client.statut_client || 'a_verifier';
                                            const options = [current];
                                            if (current === 'a_verifier' || current === 'pret') options.push('annule');
                                            else if (current === 'annule') options.push('a_verifier');
                                            return options.map(v => {
                                                const info = CLIENT_STATUTS[v];
                                                if (!info) return null;
                                                return (
                                                    <SelectItem key={v} value={v}>
                                                        <span className={`inline-block px-2 py-0.5 rounded text-sm ${info.color}`}>{info.label}</span>
                                                    </SelectItem>
                                                );
                                            });
                                        })()}
                                    </SelectContent>
                                </Select>
                                <Badge variant="outline" className={`${statutInfo.color} border text-sm px-3 py-1`}>{statutInfo.label}</Badge>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 2: Contact client */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <User size={16} className="text-[#2e7cd6]" />
                                2. Contact client
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6">
                                <div>
                                    <span className="text-xs text-slate-400">Nom</span>
                                    <p className="text-sm font-medium text-slate-800">{client.client_nom}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Telephone</span>
                                    <p className="text-sm text-slate-700">{client.client_telephone || <span className="italic text-slate-400">-</span>}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Email</span>
                                    <p className="text-sm text-slate-700">{client.client_email || <span className="italic text-slate-400">-</span>}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Commercial</span>
                                    <p className="text-sm text-slate-700">{client.commercial_name || <span className="italic text-slate-400">Non assigne</span>}</p>
                                </div>
                                <div className="sm:col-span-2 lg:col-span-3 border-t border-slate-100 pt-3 mt-1">
                                    <div className="flex items-stretch gap-0 rounded-lg border border-slate-200 overflow-hidden">
                                        <div className="flex-1 px-4 py-3 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <MapPin size={12} className="text-emerald-500 shrink-0" />
                                                <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Depart</span>
                                            </div>
                                            <p className="text-sm text-slate-800">{client.adresse_depart?.adresse || <span className="italic text-slate-400">Non renseignee</span>}</p>
                                        </div>
                                        <div className="flex items-center justify-center px-4 bg-slate-50 border-x border-slate-200">
                                            <div className="flex items-center gap-1 text-slate-400">
                                                <div className="w-4 h-[1.5px] bg-slate-300 rounded" />
                                                <div className="w-4 h-[1.5px] bg-slate-300 rounded" />
                                                <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[7px] border-l-[#2e7cd6]" />
                                            </div>
                                        </div>
                                        <div className="flex-1 px-4 py-3 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <MapPin size={12} className="text-red-500 shrink-0" />
                                                <span className="text-xs font-medium text-red-600 uppercase tracking-wide">Arrivee</span>
                                            </div>
                                            <p className="text-sm text-slate-800">{client.adresse_arrivee?.adresse || <span className="italic text-slate-400">Non renseignee</span>}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section Remarque accepté */}
                    <Card className="border-slate-200" data-testid="client-remarque-section">
                        <CardContent className="py-3 px-4">
                            <div className="flex items-start gap-2">
                                <MessageSquare size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                                {editingRemarque ? (
                                    <div className="flex-1 flex items-start gap-2">
                                        <Textarea
                                            value={remarqueAccepteValue}
                                            onChange={e => {
                                                setRemarqueAccepteValue(e.target.value);
                                                // Auto-grow vertical : pas de limite de lignes
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }}
                                            ref={el => {
                                                if (el && editingRemarque) {
                                                    el.style.height = 'auto';
                                                    el.style.height = `${el.scrollHeight}px`;
                                                }
                                            }}
                                            rows={3}
                                            className="text-sm flex-1 resize-y overflow-hidden"
                                            autoFocus
                                            disabled={savingRemarque}
                                            data-testid="client-remarque-input"
                                            placeholder="Entrée pour aller à la ligne · Ctrl+Entrée pour valider"
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                                    e.preventDefault();
                                                    saveRemarqueAccepte();
                                                }
                                                if (e.key === 'Escape') setEditingRemarque(false);
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                saveRemarqueAccepte();
                                            }}
                                            disabled={savingRemarque}
                                            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-emerald-600 hover:bg-emerald-50 active:bg-emerald-100 disabled:opacity-50 transition-colors touch-manipulation shrink-0"
                                            data-testid="client-remarque-save"
                                            aria-label="Valider la remarque"
                                        >
                                            {savingRemarque ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setEditingRemarque(false);
                                            }}
                                            disabled={savingRemarque}
                                            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-slate-400 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 transition-colors touch-manipulation shrink-0"
                                            data-testid="client-remarque-cancel"
                                            aria-label="Annuler"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2 flex-1 min-w-0">
                                        <span className="text-sm text-slate-700 whitespace-pre-wrap break-words flex-1">
                                            <strong className="text-emerald-700">Remarque :</strong> {client.remarque_accepte || '-'}
                                        </span>
                                        <button
                                            onClick={() => { setEditingRemarque(true); setRemarqueAccepteValue(client.remarque_accepte || ''); }}
                                            className="text-slate-400 hover:text-[#2e7cd6] transition-colors mt-0.5 shrink-0"
                                            data-testid="client-remarque-edit"
                                        >
                                            <Pencil size={13} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 3: Devis accepte */}
                    <Card className="border-slate-200" data-testid="client-devis-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <FileText size={16} className={formuleColors.icon} />
                                3. Devis accepte — {formuleLabel}
                                <Badge variant="outline" className={`${formuleColors.bg} ${formuleColors.text} ${formuleColors.border} border ml-2`}>
                                    Accepte
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="p-4 rounded-lg border border-emerald-500 bg-emerald-50/30">
                                <div className="flex items-center gap-1.5 mb-3">
                                    <CheckCircle2 size={12} className="text-emerald-600" />
                                    <span className="text-xs font-medium text-emerald-600">Devis accepte</span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 sm:gap-4 items-center">
                                    <div className="min-w-0">
                                        <span className="text-xs text-slate-400">N Devis</span>
                                        <p className="font-mono text-sm font-medium text-slate-800 truncate">{client.numero_devis}</p>
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-xs text-slate-400">Cree le</span>
                                        <p className="text-sm text-slate-700 truncate">{formatDate(devis?.created_at || client.created_at)}</p>
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-xs text-slate-400">Formule</span>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <Badge variant="outline" className={`${formuleColors.bg} ${formuleColors.text} ${formuleColors.border} border text-xs`}>
                                                {formuleLabel}
                                            </Badge>
                                            {devis?.parametres?.volume_devis ? (
                                                <span className="text-xs text-slate-600 whitespace-nowrap" data-testid="client-devis-volume">
                                                    · {devis.parametres.volume_devis} m³
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-xs text-slate-400">Prix</span>
                                        <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                                            {client.prix_final != null ? `${client.prix_final.toFixed(2)} EUR` : '-'}
                                        </p>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" data-testid="client-devis-actions">
                                                <MoreHorizontal size={18} className="text-slate-500" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => navigate(`/devis/${client.inventaire_id}`)} data-testid="client-devis-view">
                                                <Eye size={14} className="mr-2" /> Voir dans le dossier
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleDownloadPdf(client.devis_id)} data-testid="client-devis-download">
                                                <Download size={14} className="mr-2" /> Telecharger PDF
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                                {devis?.version > 1 && (
                                    <span className="text-xs text-slate-400 mt-1 block">v{devis.version}</span>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 4: Programmation de la prestation */}
                    {(() => {
                        const allJoursHaveDates = programmation.jours.length > 0 && programmation.jours.every(j => j.date);
                        const activeAffects = ['aot_depart', 'aot_arrivee', 'mm_depart', 'mm_arrivee', 'gm']
                            .filter(k => affectations[k]?.active);
                        const allAffectsAssigned = activeAffects.every(k => (affectations[k]?.jours || []).length > 0);
                        const isComplete = allJoursHaveDates && allAffectsAssigned;
                        return (
                    <Card id="programmation" className={`border-2 ${isComplete ? 'border-emerald-400' : 'border-red-400'} scroll-mt-4`} data-testid="client-programmation-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center justify-between text-slate-700">
                                <div className="flex items-center gap-2">
                                    <CalendarDays size={16} className="text-[#2e7cd6]" />
                                    4. Programmation de la prestation
                                </div>
                                {editingProg ? (
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => { setEditingProg(false); fetchClient(); }} data-testid="prog-cancel-btn">Annuler</Button>
                                        <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={saveProgrammation} disabled={savingProg} data-testid="prog-save-btn">
                                            {savingProg ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
                                            Enregistrer
                                        </Button>
                                    </div>
                                ) : (
                                    <Button variant="outline" size="sm" onClick={() => setEditingProg(true)} data-testid="prog-edit-btn">
                                        Modifier
                                    </Button>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {editingProg ? (
                                <>
                                    <div className="flex items-center gap-3 mb-5">
                                        <span className="text-sm text-slate-600">Nombre de jours :</span>
                                        <div className="flex items-center gap-1">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleNbJoursChange(programmation.nb_jours - 1)} data-testid="prog-minus">
                                                <Minus size={14} />
                                            </Button>
                                            <Input type="number" min={0} max={30} value={programmation.nb_jours} onChange={e => handleNbJoursChange(parseInt(e.target.value) || 0)} className="w-16 h-8 text-center" data-testid="prog-nb-jours" />
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleNbJoursChange(programmation.nb_jours + 1)} data-testid="prog-plus">
                                                <Plus size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                    {programmation.jours.length === 0 ? (
                                        <p className="text-sm text-slate-400 italic text-center py-4">Saisissez le nombre de jours pour programmer la prestation</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {programmation.jours.map((jour, idx) => {
                                                return (
                                                <div key={jour.jour} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50" data-testid={`prog-jour-${jour.jour}`}>
                                                    <p className="text-sm font-semibold text-[#2e7cd6] mb-3">Jour {jour.jour}</p>
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Date <span className="text-red-500">*</span></label>
                                                            <Input type="date" value={jour.date || ''} onChange={e => updateJour(jour.jour, 'date', e.target.value)} className="h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Heure <span className="text-red-500">*</span></label>
                                                            <Input type="time" value={jour.heure || ''} onChange={e => updateJour(jour.jour, 'heure', e.target.value)} className="h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Demenageurs <span className="text-red-500">*</span></label>
                                                            <Input type="number" min={0} value={jour.nb_demenageurs ?? ''} onChange={e => updateJour(jour.jour, 'nb_demenageurs', parseInt(e.target.value) || 0)} className="h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Camion 20m3 <span className="text-red-500">*</span></label>
                                                            <Input type="number" min={0} value={jour.camion_20m3 ?? ''} onChange={e => updateJour(jour.jour, 'camion_20m3', e.target.value === '' ? null : (parseInt(e.target.value) || 0))} className="h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Camion 30m3 <span className="text-red-500">*</span></label>
                                                            <Input type="number" min={0} value={jour.camion_30m3 ?? ''} onChange={e => updateJour(jour.jour, 'camion_30m3', e.target.value === '' ? null : (parseInt(e.target.value) || 0))} className="h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Type <span className="text-red-500">*</span></label>
                                                            <Select value={jour.type_prestation || ''} onValueChange={v => updateJour(jour.jour, 'type_prestation', v)}>
                                                                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {TYPES_PRESTATION.map(t => (
                                                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* Mode lecture */
                                programmation.jours.length === 0 ? (
                                    <p className="text-sm text-slate-400 italic text-center py-4">Aucune programmation</p>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-xs text-slate-500">{programmation.nb_jours} jour{programmation.nb_jours > 1 ? 's' : ''} programme{programmation.nb_jours > 1 ? 's' : ''}</p>
                                        {programmation.jours.map(jour => (
                                            <div key={jour.jour} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50" data-testid={`prog-view-jour-${jour.jour}`}>
                                                <p className="text-sm font-semibold text-[#2e7cd6] mb-2">Jour {jour.jour}</p>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                                    <div>
                                                        <span className="text-xs text-slate-400">Date</span>
                                                        <p className="text-sm font-medium text-slate-800">{jour.date ? formatDate(jour.date) : '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-slate-400">Heure</span>
                                                        <p className="text-sm font-medium text-slate-800">{jour.heure || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-slate-400">Demenageurs</span>
                                                        <p className="text-sm font-medium text-slate-800">{jour.nb_demenageurs ?? '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-slate-400">Camion 20m3</span>
                                                        <p className="text-sm font-medium text-slate-800">{jour.camion_20m3 ?? 0}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-slate-400">Camion 30m3</span>
                                                        <p className="text-sm font-medium text-slate-800">{jour.camion_30m3 ?? 0}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-slate-400">Type</span>
                                                        <p className="text-sm font-medium text-slate-800">{TYPES_PRESTATION.find(t => t.value === jour.type_prestation)?.label || jour.type_prestation || '-'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            )}

                            {/* Bloc d'affectation AOT/MM/GM aux jours */}
                            {programmation.nb_jours > 0 && (affectations.aot_depart.active || affectations.aot_arrivee.active || affectations.mm_depart.active || affectations.mm_arrivee.active || affectations.gm.active) && (
                                <div className="mt-6 pt-5 border-t border-slate-200">
                                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Affectation aux jours</h4>
                                    <div className="space-y-2" data-testid="prog-affectations-block">
                                        {[
                                            { key: 'aot_depart', label: 'AOT départ' },
                                            { key: 'aot_arrivee', label: 'AOT arrivée' },
                                            { key: 'mm_depart', label: 'Monte-meuble départ' },
                                            { key: 'mm_arrivee', label: 'Monte-meuble arrivée' },
                                            { key: 'gm', label: 'Garde-meuble (entrée)' },
                                        ].filter(row => affectations[row.key]?.active).map(row => (
                                            <div key={row.key} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid={`affect-row-${row.key}`}>
                                                <span className="text-sm font-medium text-slate-700 md:w-48 shrink-0">{row.label}</span>
                                                <div className="flex flex-wrap gap-2">
                                                    {Array.from({ length: programmation.nb_jours }, (_, i) => i + 1).map(jourNum => {
                                                        const checked = (affectations[row.key]?.jours || []).includes(jourNum);
                                                        return (
                                                            <label
                                                                key={jourNum}
                                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${
                                                                    checked
                                                                        ? 'bg-[#2e7cd6] text-white border-[#2e7cd6]'
                                                                        : 'bg-white text-slate-700 border-slate-300 hover:border-[#2e7cd6]'
                                                                } ${!editingProg ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                                data-testid={`affect-${row.key}-jour-${jourNum}`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    disabled={!editingProg}
                                                                    onChange={() => toggleAffectation(row.key, jourNum)}
                                                                    className="hidden"
                                                                />
                                                                Jour {jourNum}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-3 italic">
                                        Cocher un jour fixe la date de l'AOT / Monte-meuble / Garde-meuble à la date de ce jour. Cocher plusieurs jours créera plusieurs entrées (chacune avec sa propre date et son propre statut).
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                        );
                    })()}

                    {/* Section 5: Programmation livraison
                        - Sans livraison (pas de fournitures) → bordure verte (rien à programmer)
                        - Fournitures actives + formule Luxe → bordure verte (livraison incluse dans la prestation Luxe, pas besoin de programmer)
                        - Fournitures actives + livraison non programmée → bordure rouge (action requise)
                        - Livraison programmée/terminée → bordure verte */}
                    {(() => {
                        const isLuxe = client?.formule === 'luxe';
                        const livraisonOk = !livraisonData.fournitures_active || isLuxe || !!livraisonData.livraison;
                        return (
                    <Card
                        className={`border-2 ${livraisonOk ? 'border-emerald-400' : 'border-red-400'}`}
                        data-testid="client-livraison-section"
                    >
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Package size={16} className="text-[#2e7cd6]" />
                                5. Programmation livraison
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {!livraisonData.fournitures_active ? (
                                /* Pas de fournitures */
                                <div className="space-y-4">
                                    <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-slate-500">Fournitures : <span className="font-medium text-slate-700">Non</span></p>
                                                {!livraisonData.livraison && (
                                                    <Badge variant="outline" className="bg-slate-100 text-slate-600 text-xs mt-1">Sans livraison</Badge>
                                                )}
                                            </div>
                                            {!livraisonData.livraison && !showLivraisonForm && (
                                                <Button variant="outline" size="sm" onClick={() => { setShowLivraisonForm(true); setLivraisonForm({ date: '', heure: '' }); }} data-testid="livraison-add-btn">
                                                    <Plus size={14} className="mr-1.5" /> Ajouter une livraison
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Affichage de la livraison programmée (si existante) */}
                                    {livraisonData.livraison && !showLivraisonForm && (
                                        <div className={`p-4 rounded-lg border ${livraisonData.livraison.statut === 'termine' ? 'border-emerald-300 bg-emerald-50/30' : 'border-[#2e7cd6] bg-blue-50/30'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    {livraisonData.livraison.statut === 'termine' ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Clock size={18} className="text-[#2e7cd6]" />}
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-800">{formatDate(livraisonData.livraison.date)} à {livraisonData.livraison.heure}</p>
                                                        <Badge variant="outline" className={livraisonData.livraison.statut === 'termine' ? 'bg-emerald-100 text-emerald-700 text-xs mt-1' : 'bg-blue-100 text-blue-700 text-xs mt-1'}>
                                                            {livraisonData.livraison.statut === 'termine' ? 'Terminée' : 'Programmée'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                {livraisonData.livraison.statut !== 'termine' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs"
                                                        onClick={() => {
                                                            setShowLivraisonForm(true);
                                                            setLivraisonForm({
                                                                date: livraisonData.livraison.date || '',
                                                                heure: livraisonData.livraison.heure || '',
                                                            });
                                                        }}
                                                        data-testid="livraison-modifier-no-fournitures-btn"
                                                    >
                                                        Modifier
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Formulaire programmation (visible quand le bouton est cliqué) */}
                                    {showLivraisonForm && (
                                        <div className="p-4 rounded-lg border border-[#2e7cd6] bg-blue-50/20">
                                            <p className="text-sm font-medium text-slate-700 mb-3">Programmer la livraison</p>
                                            <div className="flex items-end gap-3 flex-wrap">
                                                <div>
                                                    <label className="text-xs text-slate-400 block mb-1">Date</label>
                                                    <Input type="date" value={livraisonForm.date} onChange={e => setLivraisonForm(p => ({ ...p, date: e.target.value }))} className="h-8 text-sm w-40" data-testid="livraison-date" />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-slate-400 block mb-1">Heure</label>
                                                    <Input type="time" value={livraisonForm.heure} onChange={e => setLivraisonForm(p => ({ ...p, heure: e.target.value }))} className="h-8 text-sm w-32" data-testid="livraison-heure" />
                                                </div>
                                                <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white h-8" onClick={handleConfirmLivraison} disabled={savingLivraison} data-testid="livraison-confirm-btn">
                                                    {savingLivraison ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                                                    Confirmer
                                                </Button>
                                                <Button variant="outline" size="sm" className="h-8" onClick={() => setShowLivraisonForm(false)}>Annuler</Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* Fournitures actives */
                                <div className="space-y-4">
                                    {/* Detail fournitures */}
                                    <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                                        <p className="text-xs font-medium text-slate-500 mb-3 uppercase tracking-wide">Detail des fournitures</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                            {Object.entries(livraisonData.fournitures || {}).map(([key, val]) => {
                                                if (!val || val === '0') return null;
                                                return (
                                                    <div key={key} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-slate-100">
                                                        <span className="text-xs text-slate-600">{FOURNITURE_LABELS[key] || key}</span>
                                                        <span className="text-sm font-semibold text-slate-800 ml-2">{val}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Cas Luxe : livraison incluse dans la prestation, pas besoin de programmer */}
                                    {isLuxe ? (
                                        <>
                                            <div className="p-4 rounded-lg border-2 border-emerald-400 bg-emerald-50/40 flex items-center justify-between" data-testid="livraison-luxe-banner">
                                                <div className="flex items-center gap-3">
                                                    <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                                                    <div>
                                                        <p className="text-sm font-semibold text-emerald-800">Formule Luxe — sans livraison</p>
                                                        <p className="text-xs text-emerald-700/80">La livraison des fournitures est incluse dans la prestation Luxe.</p>
                                                    </div>
                                                </div>
                                                <Button variant="outline" size="sm" onClick={() => { setShowLivraisonForm(true); setLivraisonForm({ date: '', heure: '' }); }} data-testid="livraison-luxe-add-btn">
                                                    <Plus size={14} className="mr-1.5" /> Ajouter une livraison
                                                </Button>
                                            </div>

                                            {/* Si une livraison a été programmée malgré tout, on l'affiche */}
                                            {livraisonData.livraison && (
                                                <div className={`p-4 rounded-lg border ${livraisonData.livraison.statut === 'termine' ? 'border-emerald-300 bg-emerald-50/30' : 'border-[#2e7cd6] bg-blue-50/30'}`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {livraisonData.livraison.statut === 'termine' ? (
                                                                <CheckCircle2 size={16} className="text-emerald-600" />
                                                            ) : (
                                                                <Clock size={16} className="text-[#2e7cd6]" />
                                                            )}
                                                            <div>
                                                                <p className="text-sm font-medium text-slate-800">
                                                                    Livraison {livraisonData.livraison.statut === 'termine' ? 'terminee' : 'programmee'}
                                                                </p>
                                                                <p className="text-xs text-slate-500">
                                                                    {formatDate(livraisonData.livraison.date)} a {livraisonData.livraison.heure}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline" className={livraisonData.livraison.statut === 'termine' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                                                                {livraisonData.livraison.statut === 'termine' ? 'Terminee' : 'Programmee'}
                                                            </Badge>
                                                            {livraisonData.livraison.statut !== 'termine' && (
                                                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setShowLivraisonForm(true); setLivraisonForm({ date: livraisonData.livraison.date || '', heure: livraisonData.livraison.heure || '' }); }} data-testid="livraison-modifier-btn">
                                                                    Modifier
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : livraisonData.livraison ? (
                                        <div className={`p-4 rounded-lg border ${livraisonData.livraison.statut === 'termine' ? 'border-emerald-300 bg-emerald-50/30' : 'border-[#2e7cd6] bg-blue-50/30'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    {livraisonData.livraison.statut === 'termine' ? (
                                                        <CheckCircle2 size={16} className="text-emerald-600" />
                                                    ) : (
                                                        <Clock size={16} className="text-[#2e7cd6]" />
                                                    )}
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-800">
                                                            Livraison {livraisonData.livraison.statut === 'termine' ? 'terminee' : 'programmee'}
                                                        </p>
                                                        <p className="text-xs text-slate-500">
                                                            {formatDate(livraisonData.livraison.date)} a {livraisonData.livraison.heure}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className={livraisonData.livraison.statut === 'termine' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                                                        {livraisonData.livraison.statut === 'termine' ? 'Terminee' : 'Programmee'}
                                                    </Badge>
                                                    {livraisonData.livraison.statut !== 'termine' && (
                                                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setShowLivraisonForm(true); setLivraisonForm({ date: livraisonData.livraison.date || '', heure: livraisonData.livraison.heure || '' }); }} data-testid="livraison-modifier-btn">
                                                            Modifier
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/30">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <Badge variant="outline" className="bg-amber-100 text-amber-700 text-xs">A programmer</Badge>
                                                </div>
                                                <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={() => { setShowLivraisonForm(true); setLivraisonForm({ date: '', heure: '' }); }} data-testid="livraison-programmer-btn">
                                                    <CalendarDays size={14} className="mr-1.5" /> Programmer une livraison
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Formulaire programmation */}
                                    {showLivraisonForm && (
                                        <div className="p-4 rounded-lg border border-[#2e7cd6] bg-blue-50/20">
                                            <p className="text-sm font-medium text-slate-700 mb-3">Programmer la livraison</p>
                                            <div className="flex items-end gap-3">
                                                <div>
                                                    <label className="text-xs text-slate-400 block mb-1">Date</label>
                                                    <Input type="date" value={livraisonForm.date} onChange={e => setLivraisonForm(p => ({ ...p, date: e.target.value }))} className="h-8 text-sm w-40" data-testid="livraison-date" />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-slate-400 block mb-1">Heure</label>
                                                    <Input type="time" value={livraisonForm.heure} onChange={e => setLivraisonForm(p => ({ ...p, heure: e.target.value }))} className="h-8 text-sm w-32" data-testid="livraison-heure" />
                                                </div>
                                                <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white h-8" onClick={handleConfirmLivraison} disabled={savingLivraison} data-testid="livraison-confirm-btn">
                                                    {savingLivraison ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                                                    Confirmer
                                                </Button>
                                                <Button variant="outline" size="sm" className="h-8" onClick={() => setShowLivraisonForm(false)}>Annuler</Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Livraisons supplémentaires (visibles uniquement si la principale est terminée) */}
                                    {livraisonData.livraison && livraisonData.livraison.statut === 'termine' && (
                                        <div className="space-y-2 pt-2 border-t border-slate-200">
                                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Livraisons supplémentaires</p>
                                            {(livraisonData.livraisons_supp || []).length === 0 ? (
                                                <p className="text-xs text-slate-400">Aucune livraison supplémentaire</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {(livraisonData.livraisons_supp || []).map(ls => (
                                                        <div key={ls.id} className={`p-3 rounded-lg border ${ls.statut === 'termine' ? 'border-emerald-300 bg-emerald-50/30' : 'border-[#2e7cd6] bg-blue-50/30'}`} data-testid={`livraison-supp-${ls.id}`}>
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    {ls.statut === 'termine' ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Clock size={14} className="text-[#2e7cd6]" />}
                                                                    <p className="text-sm text-slate-700">{formatDate(ls.date)} a {ls.heure}</p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant="outline" className={ls.statut === 'termine' ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-blue-100 text-blue-700 text-xs'}>
                                                                        {ls.statut === 'termine' ? 'Terminee' : 'Programmee'}
                                                                    </Badge>
                                                                    {ls.statut !== 'termine' && (
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-7 text-xs"
                                                                            onClick={() => {
                                                                                setEditingLivraisonSuppId(ls.id);
                                                                                setLivraisonSuppForm({ date: ls.date || '', heure: ls.heure || '', remarque: ls.remarque || '' });
                                                                                setShowLivraisonSuppForm(true);
                                                                            }}
                                                                            data-testid={`livraison-supp-modifier-${ls.id}`}
                                                                        >
                                                                            Modifier
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {ls.remarque && (
                                                                <p className="mt-2 text-xs text-slate-600 whitespace-pre-wrap bg-white/60 border-l-2 border-[#2e7cd6] rounded-r px-2 py-1" data-testid={`livraison-supp-remarque-${ls.id}`}>
                                                                    {ls.remarque}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {!showLivraisonSuppForm ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="border-dashed border-[#2e7cd6] text-[#2e7cd6] hover:bg-blue-50"
                                                    onClick={() => { setShowLivraisonSuppForm(true); setEditingLivraisonSuppId(null); setLivraisonSuppForm({ date: '', heure: '', remarque: '' }); }}
                                                    data-testid="livraison-supp-add-btn"
                                                >
                                                    <Plus size={14} className="mr-1.5" /> Rajouter une livraison supplémentaire
                                                </Button>
                                            ) : (
                                                <div className="p-3 rounded-lg border border-[#2e7cd6] bg-blue-50/20">
                                                    <p className="text-sm font-medium text-slate-700 mb-2">{editingLivraisonSuppId ? 'Modifier la livraison supplémentaire' : 'Nouvelle livraison supplémentaire'}</p>
                                                    <div className="flex items-end gap-2 flex-wrap">
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Date</label>
                                                            <Input type="date" value={livraisonSuppForm.date} onChange={e => setLivraisonSuppForm(p => ({ ...p, date: e.target.value }))} className="h-8 text-sm w-40" data-testid="livraison-supp-date" />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-slate-400 block mb-1">Heure</label>
                                                            <Input type="time" value={livraisonSuppForm.heure} onChange={e => setLivraisonSuppForm(p => ({ ...p, heure: e.target.value }))} className="h-8 text-sm w-32" data-testid="livraison-supp-heure" />
                                                        </div>
                                                        <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white h-8" onClick={handleConfirmLivraisonSupp} disabled={savingLivraisonSupp} data-testid="livraison-supp-confirm-btn">
                                                            {savingLivraisonSupp ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                                                            Confirmer
                                                        </Button>
                                                        <Button variant="outline" size="sm" className="h-8" onClick={() => { setShowLivraisonSuppForm(false); setEditingLivraisonSuppId(null); }}>Annuler</Button>
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="text-xs text-slate-400 block mb-1">Remarque (optionnelle)</label>
                                                        <Textarea
                                                            value={livraisonSuppForm.remarque}
                                                            onChange={e => setLivraisonSuppForm(p => ({ ...p, remarque: e.target.value }))}
                                                            rows={2}
                                                            placeholder="Ajouter une remarque (visible dans l'agenda)..."
                                                            className="text-sm"
                                                            data-testid="livraison-supp-remarque"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                        );
                    })()}

                    {/* Section 6: Acompte */}
                    {(() => {
                        const hasAcompte = acomptePaiements.length > 0 || client?.acompte === 'effectue';
                        const acompteBorder = hasAcompte ? 'border-emerald-400' : 'border-amber-400';
                        return (
                            <Card className={`border-2 ${acompteBorder}`} data-testid="client-acompte-section">
                                <CardHeader className="pb-2 border-b border-slate-100">
                                    <CardTitle className="text-sm font-semibold flex items-center justify-between text-slate-700">
                                            <div className="flex items-center gap-2">
                                                <Euro size={16} className="text-[#2e7cd6]" />
                                                6. Acompte
                                                {hasAcompte ? (
                                                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 ml-1 text-xs">
                                                        Acompte regle
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 ml-1 text-xs">
                                                        A regler
                                                    </Badge>
                                                )}
                                                {(() => {
                                                    const prix = client?.prix_final;
                                                    if (prix == null) return null;
                                                    const paye = (acomptePaiements || []).reduce((s, p) => s + (Number(p.montant) || 0), 0);
                                                    const reste = Math.max(0, prix - paye);
                                                    if (reste <= 0) return null;
                                                    return (
                                                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 ml-1 text-xs" data-testid="client-acompte-solde-restant">
                                                            Solde restant : {reste.toFixed(2)} EUR
                                                        </Badge>
                                                    );
                                                })()}
                                            </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-4">
                                    {hasAcompte ? (
                                        <div className="space-y-2">
                                            {acomptePaiements.map(p => (
                                                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                                            <CreditCard size={14} className="text-emerald-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-emerald-700">{p.montant?.toFixed(2)} EUR</p>
                                                            <span className="text-xs text-slate-500">{TYPE_PAIEMENT_LABELS[p.type_paiement] || p.type_paiement} — {formatDate(p.date_paiement)}</span>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-slate-400">par {p.cree_par}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 italic text-center py-4">Aucun acompte enregistre — l'ajout se fait depuis la page Facturation</p>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })()}

                    {/* Section 7: AOT */}
                    {(() => {
                        const aotAllAccepted = aotList.length > 0 && aotList.every(a => a.statut === 'accepte');
                        const aotBorder = aotList.length === 0 ? 'border-2 border-emerald-400' : aotAllAccepted ? 'border-2 border-emerald-400' : 'border-2 border-red-400';
                        return (
                    <Card className={aotBorder} data-testid="client-aot-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <ShieldCheck size={16} className="text-[#2e7cd6]" />
                                7. AOT
                                {aotList.length > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 ml-1">{aotList.length}</Badge>}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {aotList.length === 0 ? (
                                <p className="text-sm text-slate-400 italic text-center py-4">Aucune AOT</p>
                            ) : (
                                <div className="space-y-2">
                                    {aotList.map(aot => {
                                        const statuts = { a_demander: { label: 'A demander', color: 'bg-amber-100 text-amber-700 border-amber-200' }, hors_delais: { label: 'Hors delais', color: 'bg-red-100 text-red-700 border-red-200' }, en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' }, accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' }, refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700 border-red-200' } };
                                        const s = statuts[aot.statut] || statuts.a_demander;
                                        return (
                                            <div key={aot.id} className="p-3 rounded-lg border border-slate-200 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <Badge variant="outline" className={`text-xs border ${aot.type === 'depart' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                                        {aot.type === 'depart' ? 'Depart' : 'Arrivee'}
                                                    </Badge>
                                                    <div>
                                                        <p className="text-sm text-slate-700">{aot.adresse || '-'}</p>
                                                        <span className="text-xs text-slate-400">{formatDate(aot.date_demenagement)}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {aot.has_file && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 text-xs gap-1.5"
                                                            onClick={async () => {
                                                                try {
                                                                    const res = await axios.get(`${API}/api/aot/${aot.id}/file`, { withCredentials: true, responseType: 'blob' });
                                                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                                                    const a = document.createElement('a');
                                                                    a.href = url;
                                                                    a.download = aot.aot_filename || 'AOT.pdf';
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    a.remove();
                                                                    window.URL.revokeObjectURL(url);
                                                                } catch { toast.error('Erreur téléchargement AOT'); }
                                                            }}
                                                            data-testid={`client-aot-download-${aot.id}`}
                                                        >
                                                            <Download size={13} className="text-[#2e7cd6]" />
                                                            AOT
                                                        </Button>
                                                    )}
                                                    <Badge variant="outline" className={`text-xs border ${s.color}`}>{s.label}</Badge>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                        );
                    })()}

                    {/* Section 8: Monte-meuble */}
                    {(() => {
                        const mmAllDone = monteMeubleList.length > 0 && monteMeubleList.every(mm => mm.statut === 'reserve' || mm.statut === 'termine');
                        const mmBorder = monteMeubleList.length === 0 ? 'border-2 border-emerald-400' : mmAllDone ? 'border-2 border-emerald-400' : 'border-2 border-red-400';
                        return (
                    <Card className={mmBorder} data-testid="client-monte-meuble-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Truck size={16} className="text-[#2e7cd6]" />
                                8. Monte-meuble
                                {monteMeubleList.length > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 ml-1">{monteMeubleList.length}</Badge>}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {monteMeubleList.length === 0 ? (
                                <p className="text-sm text-slate-400 italic text-center py-4">Aucun monte-meuble</p>
                            ) : (
                                <div className="space-y-2">
                                    {monteMeubleList.map(mm => {
                                        const statuts = { a_reserver: { label: 'A reserver', color: 'bg-amber-100 text-amber-700 border-amber-200' }, reserve: { label: 'Reserve', color: 'bg-blue-100 text-blue-700 border-blue-200' }, termine: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' } };
                                        const s = statuts[mm.statut] || statuts.a_reserver;
                                        return (
                                            <div key={mm.id} className="p-3 rounded-lg border border-slate-200 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <Badge variant="outline" className={`text-xs border ${mm.type === 'depart' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                                        {mm.type === 'depart' ? 'Depart' : 'Arrivee'}
                                                    </Badge>
                                                    <div>
                                                        <p className="text-sm text-slate-700">{mm.adresse || '-'}</p>
                                                        <span className="text-xs text-slate-400">{formatDate(mm.date_demenagement)}</span>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className={`text-xs border ${s.color}`}>{s.label}</Badge>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                        );
                    })()}

                    {/* Section 9: Garde-meuble */}
                    {(() => {
                        const gmAllDone = gardeMeubleList.length > 0 && gardeMeubleList.every(gm => gm.statut !== 'a_prevoir');
                        const gmBorder = gardeMeubleList.length === 0 ? 'border-2 border-emerald-400' : gmAllDone ? 'border-2 border-emerald-400' : 'border-2 border-red-400';
                        return (
                    <Card className={gmBorder} data-testid="client-garde-meuble-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Warehouse size={16} className="text-[#2e7cd6]" />
                                9. Garde-meuble
                                {gardeMeubleList.length > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 ml-1">{gardeMeubleList.length}</Badge>}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {gardeMeubleList.length === 0 ? (
                                <p className="text-sm text-slate-400 italic text-center py-4">Aucun garde-meuble</p>
                            ) : (
                                <div className="space-y-2">
                                    {gardeMeubleList.map(gm => {
                                        const statuts = { a_prevoir: { label: 'A prevoir', color: 'bg-amber-100 text-amber-700 border-amber-200' }, reserve: { label: 'Reserve', color: 'bg-blue-100 text-blue-700 border-blue-200' }, actif: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' }, termine: { label: 'Termine', color: 'bg-slate-100 text-slate-600 border-slate-200' } };
                                        const s = statuts[gm.statut] || statuts.a_prevoir;
                                        return (
                                            <div key={gm.id} className="p-3 rounded-lg border border-slate-200 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div>
                                                        <p className="text-sm text-slate-700">Entree : {formatDate(gm.date_entree)}</p>
                                                        <div className="flex items-center gap-3 mt-0.5">
                                                            {gm.date_sortie && <span className="text-xs text-slate-400">Sortie : {formatDate(gm.date_sortie)}</span>}
                                                            {gm.garde_meuble_tarif && <span className="text-xs text-slate-400">Tarif : {gm.garde_meuble_tarif} EUR/mois</span>}
                                                            {gm.garde_meuble_duree && <span className="text-xs text-slate-400">Duree : {gm.garde_meuble_duree}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className={`text-xs border ${s.color}`}>{s.label}</Badge>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                        );
                    })()}

                </div>
            </div>

            </ReadOnlyGuard>

            {/* PDF Preview Modal — rendu hors du ReadOnlyGuard pour que les boutons
                Telecharger / Retour restent cliquables, même en mode lecture seule */}
            {pdfPreview && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="pdf-preview-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {pdfPreview.title}
                            </h2>
                            <div className="flex items-center gap-2">
                                <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleDownloadPreview} data-testid="pdf-download-btn">
                                    <Download size={15} className="mr-1.5" />
                                    Telecharger
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleClosePreview} data-testid="pdf-close-btn">
                                    <ArrowLeft size={15} />
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <iframe src={pdfPreview.url} className="w-full h-full border-0" title="Apercu PDF" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
